// ObserveView.tsx - the locked dx4 Observe surface (S20). The street stepper walks Preflop -> River;
// each street re-renders the felt (engine bridge), the pro's narration beats, the evidence ladder, and
// the five hand-family weights, with a river takeaway recap. Read-first: no decision to make, tap any
// term to learn. Deep-linkable at ?mode=observe&street=0..3.
//
// The felt comes straight from the S19 engine bridge (teachingFeltView); the narration is authored
// teaching content modeled on the coach HandNarration contract (see narration.ts + data.ts). The
// buildHandNarration prose lane (/api/coach) is the generated-hand path and is not invoked here.
import { useEffect, useMemo, useState } from 'react';
import { OvalFelt } from '../components/OvalFelt';
import { StreetStepper, STREET_LABELS } from '../components/StreetStepper';
import { Term } from '../components/Glossary';
import { teachingFeltView } from '../engine/bridge';
import { OBSERVE_STREETS, OBSERVE_RECAP } from './data';
import { NarrationBeats } from './NarrationBeats';
import { EvidenceLadder } from './EvidenceLadder';
import { HandFamilyWeights } from './HandFamilyWeights';

const LAST = OBSERVE_STREETS.length - 1;

/** Read the initial street from ?street=0..3 (clamped); default preflop. */
function initialStreet(): number {
  if (typeof window === 'undefined') return 0;
  const raw = Number(new URLSearchParams(window.location.search).get('street'));
  return Number.isInteger(raw) && raw >= 0 && raw <= LAST ? raw : 0;
}

/** Keep the URL in sync so a street is deep-linkable and shareable. */
function syncUrl(street: number) {
  if (typeof window === 'undefined') return;
  const q = new URLSearchParams();
  q.set('mode', 'observe');
  q.set('street', String(street));
  window.history.replaceState(null, '', '?' + q.toString());
}

export function ObserveView() {
  const [street, setStreet] = useState(initialStreet);
  const data = OBSERVE_STREETS[street];
  const felt = useMemo(() => teachingFeltView(street), [street]);
  const isRiver = street === LAST;

  useEffect(() => {
    syncUrl(street);
    window.scrollTo({ top: 0 });
  }, [street]);

  const go = (i: number) => setStreet(Math.max(0, Math.min(LAST, i)));

  return (
    <>
      <OvalFelt view={felt} />

      <div className="body">
        <div>
          <div className="kicker">{data.kicker}</div>
          <div className="h2">{data.h2}</div>
          <div className="lede">{data.lede}</div>
        </div>

        <StreetStepper street={street} onSelect={go} />

        <NarrationBeats beats={data.beats} />

        {/* evidence ladder - hidden preflop (nothing to be sure of yet) */}
        {data.level > 0 && (
          <div className="sec">
            <div className="stt-row">
              <span className="bar" />
              <div>
                <div className="stt">How sure the read should be</div>
                <div className="sd">
                  The <Term term="ladder">evidence ladder</Term>. Each street climbs one rung as the same
                  story repeats.
                </div>
              </div>
            </div>
            <EvidenceLadder level={data.level} />
          </div>
        )}

        {/* five hand-family weights */}
        <div className="sec">
          <div className="stt-row">
            <span className="bar" />
            <div>
              <div className="stt">{data.bucketTitle}</div>
              <div className="sd">{data.bucketSub}</div>
            </div>
          </div>
          <HandFamilyWeights families={data.families} />
        </div>

        {/* river takeaway recap */}
        {isRiver && (
          <div className="recap">
            <div className="rt">{OBSERVE_RECAP.title}</div>
            <ol>
              {OBSERVE_RECAP.rows.map((r) => (
                <li key={r.street}>
                  <b>{r.street}</b>
                  <span>{r.text}</span>
                </li>
              ))}
            </ol>
            <div className="rclose">{OBSERVE_RECAP.close}</div>
          </div>
        )}
      </div>

      <div className="dock">
        <div className="dockrow">
          <button type="button" className="cta ghost prev" disabled={street === 0} onClick={() => go(street - 1)}>
            Back
          </button>
          <button
            type="button"
            className={'cta' + (isRiver ? ' ghost' : '')}
            onClick={() => go(isRiver ? 0 : street + 1)}
          >
            {isRiver ? 'Watch another hand' : `Next: the ${STREET_LABELS[street + 1].toLowerCase()}`}
          </button>
        </div>
      </div>
    </>
  );
}
